import { useTranslation } from "react-i18next"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Link, useLocation } from "react-router-dom"
import { data } from "@/constants/sidebar"
import { useAddBrokerIdToLink } from "@/hooks/use-broker-links"
import {
  handleToggleSidebar,
  isLinkActive,
} from "@/utils/sidebar"
import { ChevronRight, Shield } from "lucide-react"
import { useAppConfig } from "@/providers/config-provider"

export const PortaAdminSidebarMenu = () => {
  const { t } = useTranslation()
  const ADMIN_TEXT = t("sidebar.administration")
  const { toggleSidebar, isMobile, state } = useSidebar()
  const location = useLocation()
  const pathname = location.pathname
  const { addBrokerIdToUrl } = useAddBrokerIdToLink()
  const config = useAppConfig()
  const primaryBg = config?.visualPreferences?.layout?.colors?.buttonConfirmBackground
  const primaryText = config?.visualPreferences?.layout?.colors?.buttonConfirmText

  // When sidebar is collapsed, show dropdown menu
  if (state === "collapsed" && !isMobile) {
    return (
      <SidebarMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              tooltip={ADMIN_TEXT}
              className="w-full"
            >
              <Shield />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="start"
            className="min-w-48"
          >
            {data.sidebarSuperAdminLinks.map((link) => {
              const isActive = isLinkActive(link.href, link.mainPath, pathname)
              return (
                <DropdownMenuItem key={link.href} asChild>
                  <Link
                    to={addBrokerIdToUrl(link.href)}
                    className="flex items-center gap-2"
                    style={isActive && primaryBg ? { backgroundColor: primaryBg, color: primaryText } : undefined}
                    onClick={() =>
                      handleToggleSidebar(
                        isMobile,
                        toggleSidebar
                      )
                    }
                  >
                    <link.icon className="h-4 w-4" />
                    <span>{link.label}</span>
                  </Link>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <Collapsible
        asChild
        defaultOpen={true}
        className="group/collapsible"
      >
        <SidebarMenuSubItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip={ADMIN_TEXT}>
              <Shield />
              <span>{ADMIN_TEXT}</span>
              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {data.sidebarSuperAdminLinks.map((link) => (
                <SidebarMenuSubItem key={link.href}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isLinkActive(
                      link.href,
                      link.mainPath,
                      pathname
                    )}
                    onClick={() =>
                      handleToggleSidebar(
                        isMobile,
                        toggleSidebar
                      )
                    }
                  >
                    <Link
                      to={addBrokerIdToUrl(link.href)}
                    >
                      <link.icon />
                      <span>{link.label}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuSubItem>
      </Collapsible>
    </SidebarMenu>
  )
}
